import { applyMiddleware, createStore } from 'redux';
import { combineReducers } from 'redux';
import { persistStore, persistReducer } from 'redux-persist';
import storageSession from 'redux-persist/lib/storage/session';
import thunk from 'redux-thunk';
import { composeWithDevTools } from 'redux-devtools-extension';

import signInReducer from '../reducers/signInReducer';
import pendingReducer from '../reducers/getPendingData';
import tabsReducer from '../reducers/tabs';
import queueReducer from '../reducers/getQueue';
import allErrorsReducer from '../reducers/getAllErrors';
import allUsersReducer from '../reducers/getAllUsers';
import UserInformationReducer from '../reducers/userInformation';
import manualUsersReducer from '../reducers/getManualUsers';
import allVendorsReducers from '../reducers/getAllVendors';
import filterReducer from '../reducers/getFilters';
import dashboardReducer from '../reducers/dashboard';
import DocumentCountReducer from '../reducers/documentCounts';
import tableFiltersReducer from '../reducers/tableFilters';
import jobBudgetReducer from '../reducers/jobBudget';
import foundationConnectionDetailsReducer from '../reducers/foundationConnectionDetails';
import syncedDataReducer from '../reducers/syncedData';
import fieldConfigReducer from '../reducers/fieldConfig';
import { getPersistKey } from '../../utils/tabSession';

const middleware = [thunk];

const appReducer = combineReducers({
  signIn: signInReducer,
  queue: queueReducer,
  pending: pendingReducer,
  errors: allErrorsReducer,
  users: allUsersReducer,
  userInfo: UserInformationReducer,
  tabs: tabsReducer,
  manualUsers: manualUsersReducer,
  vendor: allVendorsReducers,
  filters: filterReducer,
  dashboard: dashboardReducer,
  docCounts: DocumentCountReducer,
  table: tableFiltersReducer,
  jobBudget: jobBudgetReducer,
  foundationConnectionDetails: foundationConnectionDetailsReducer,
  syncedData: syncedDataReducer,
  fieldConfig: fieldConfigReducer
});

// On RESET_STORE pass undefined so every slice returns its initialState
const Reducer = (state, action) => {
  if (action.type === 'RESET_STORE') {
    return appReducer(undefined, action);
  }
  return appReducer(state, action);
};

// Each tab gets its own persist key in sessionStorage → completely independent
// Redux stores per tab, and nothing survives the tab closing.
//
// Both halves of this matter. Previously the key was the bare identifier `root`,
// which resolved to window.root (the <div id="root"> from index.html) and
// stringified to a single constant key; combined with localStorage that gave
// EVERY tab and EVERY account on the browser one shared bucket. A user with
// logins at two companies would rehydrate the other company's userInfo and
// syncedData (jobs, cost codes, GL accounts) — including after the first
// company's tab had been closed, because localStorage outlives the tab.
// sessionStorage also matches where the auth token lives (services/common.js),
// so identity and identity's cached data now share the same scope.
const persistConfig = {
  key: getPersistKey(),
  storage: storageSession
};

// One-time cleanup of the pre-fix bucket. Every existing user still has
// `persist:[object HTMLDivElement]` sitting in localStorage holding whichever
// company they last used. Nothing reads it any more, but it is stale customer
// data parked in a browser, and it would come back to life if the storage
// change were ever reverted. Drops only `persist:`-prefixed keys, so the theme
// and anything else in localStorage is left alone.
try {
  Object.keys(localStorage)
    .filter((key) => key.startsWith('persist:'))
    .forEach((key) => localStorage.removeItem(key));
} catch (e) {
  // Storage can throw in private-browsing modes; a failed cleanup is harmless.
}
const persistedReducer = persistReducer(persistConfig, Reducer);
export const store = createStore(
  persistedReducer,
  composeWithDevTools(applyMiddleware(...middleware))
);
export const persistor = persistStore(store);
